/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Button, Text, theme } from "@nordcloud/gnui";

type MultiValuePopoverProps<TKey extends string> = {
  filterKey: TKey;
  values: string[];
  valueLabels?: string[];
  hoveredValueKey: string | null;
  onValueMouseEnter: (key: string) => void;
  onValueMouseLeave: () => void;
  onRemoveFilterValue: (key: TKey, value: string) => void;
};

export function MultiValuePopover<TKey extends string>({
  filterKey,
  values,
  valueLabels,
  hoveredValueKey,
  onValueMouseEnter,
  onValueMouseLeave,
  onRemoveFilterValue,
}: MultiValuePopoverProps<TKey>) {
  return (
    <>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: "100%",
          height: theme.spacing.spacing01,
          minWidth: "160px",
          width: "100%",
          backgroundColor: "transparent",
          zIndex: theme.zindex.topoftheworld,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 0,
          top: `calc(100% + ${theme.spacing.spacing01})`,
          backgroundColor: theme.color.background.ui01,
          border: `1px solid ${theme.color.border.border01}`,
          borderRadius: theme.radius.sm,
          boxShadow: theme.shadow.shadow04,
          padding: theme.spacing.spacing02,
          zIndex: theme.zindex.topoftheworld,
          minWidth: "160px",
        }}
      >
        <div
          style={{
            maxHeight: values.length > 5 ? "130px" : "auto",
            overflowY: values.length > 5 ? "auto" : "visible",
            paddingRight: values.length > 5 ? theme.spacing.spacing01 : 0,
          }}
        >
          {values.map((value, index) => {
            const displayValue = valueLabels?.[index] ?? value;
            const valueKey = `${filterKey}-${value}`;
            return (
              <div
                key={valueKey}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: theme.spacing.spacing02,
                  padding: `${theme.spacing.spacing01} ${theme.spacing.spacing01}`,
                  borderRadius: theme.radius.sm,
                  backgroundColor:
                    hoveredValueKey === valueKey
                      ? theme.color.background.ui04
                      : "transparent",
                  cursor: "default",
                }}
                onMouseEnter={() => onValueMouseEnter(valueKey)}
                onMouseLeave={onValueMouseLeave}
              >
                <Text tag="span" size="sm">
                  {displayValue}
                </Text>
                <Button
                  severity="low"
                  size="sm"
                  icon="trash"
                  aria-label={`remove-filter-value-${value}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemoveFilterValue(filterKey, value);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    minHeight: "auto",
                    padding: 0,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
